import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import Autocomplete, {
  AutocompleteRenderGroupParams,
} from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLazyQuery } from "@apollo/client";
import { PRODUCT_NAME_QUERY } from "../../../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import { useDebounce } from "../../../hooks/useDebounce";
import { lightScrollbarStyle } from "../../../styles/lightScrollbarStyle";
import type { ProductOption } from "./types";

interface ProductQueryResponse {
  product: {
    data: Array<{
      id: number;
      name: string;
      vendor?: { name: string };
    }>;
    count: number;
  };
}

interface GroupedProductAutocompleteProps {
  selectedOptions: ProductOption[];
  onOptionsSelect: (options: ProductOption[]) => void;
  placeholder?: string;
}

const MIN_CHARS = 2;

export const GroupedProductAutocomplete: FC<
  GroupedProductAutocompleteProps
> = ({ selectedOptions, onOptionsSelect, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [collapsedVendors, setCollapsedVendors] = useState<Set<string>>(
    new Set()
  );

  const debouncedInput = useDebounce(inputValue, 300);

  const [fetchProducts, { loading }] = useLazyQuery<ProductQueryResponse>(
    PRODUCT_NAME_QUERY,
    {
      context: { clientName: TAXONOMY_CLIENT },
      onCompleted: (data) => {
        const products = data?.product?.data ?? [];
        const mapped = products.map((p) => ({
          label: p.name,
          value: p.id.toString(),
          vendorName: p.vendor?.name,
        }));
        // Group consecutive items by vendor: stable-sort by vendor, preserving
        // the API's alphabetical product name order within each group.
        // This makes vendor groups appear in order of their first product.
        const vendorFirstIndex = new Map<string, number>();
        mapped.forEach((p, i) => {
          const v = p.vendorName ?? "";
          if (!vendorFirstIndex.has(v)) vendorFirstIndex.set(v, i);
        });
        mapped.sort(
          (a, b) =>
            (vendorFirstIndex.get(a.vendorName ?? "") ?? 0) -
            (vendorFirstIndex.get(b.vendorName ?? "") ?? 0)
        );
        setOptions(mapped);
      },
    }
  );

  useEffect(() => {
    if (debouncedInput.length >= MIN_CHARS) {
      fetchProducts({ variables: { name: debouncedInput } });
    } else {
      setOptions([]);
    }
  }, [debouncedInput, fetchProducts]);

  const selectedValueSet = useMemo(
    () => new Set(selectedOptions.map((o) => o.value)),
    [selectedOptions]
  );

  const toggleVendorCollapse = useCallback((vendor: string) => {
    setCollapsedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendor)) {
        next.delete(vendor);
      } else {
        next.add(vendor);
      }
      return next;
    });
  }, []);

  const vendorOptionsMap = useMemo(() => {
    const map = new Map<string, ProductOption[]>();
    for (const opt of options) {
      const vendor = opt.vendorName ?? "Unknown";
      const list = map.get(vendor);
      if (list) {
        list.push(opt);
      } else {
        map.set(vendor, [opt]);
      }
    }
    return map;
  }, [options]);

  const handleVendorCheckbox = useCallback(
    (vendor: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const vendorProducts = vendorOptionsMap.get(vendor) ?? [];
      const vendorValues = new Set(vendorProducts.map((p) => p.value));
      const allSelected = vendorProducts.every((p) =>
        selectedValueSet.has(p.value)
      );

      if (allSelected) {
        onOptionsSelect(
          selectedOptions.filter((o) => !vendorValues.has(o.value))
        );
      } else {
        const toAdd = vendorProducts.filter(
          (p) => !selectedValueSet.has(p.value)
        );
        onOptionsSelect([...selectedOptions, ...toAdd]);
      }
    },
    [vendorOptionsMap, selectedValueSet, selectedOptions, onOptionsSelect]
  );

  const getVendorCheckboxState = useCallback(
    (vendor: string) => {
      const vendorProducts = vendorOptionsMap.get(vendor) ?? [];
      if (vendorProducts.length === 0)
        return { checked: false, indeterminate: false };
      const selectedCount = vendorProducts.filter((p) =>
        selectedValueSet.has(p.value)
      ).length;
      return {
        checked: selectedCount === vendorProducts.length,
        indeterminate:
          selectedCount > 0 && selectedCount < vendorProducts.length,
      };
    },
    [vendorOptionsMap, selectedValueSet]
  );

  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const vendor = params.group;
      const isCollapsed = collapsedVendors.has(vendor);
      const { checked, indeterminate } = getVendorCheckboxState(vendor);
      const count = vendorOptionsMap.get(vendor)?.length ?? 0;

      return (
        <li key={params.key}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 0.5,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => toggleVendorCollapse(vendor)}
          >
            {isCollapsed ? (
              <ChevronRightIcon
                sx={{ fontSize: 20, color: "text.secondary" }}
              />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            )}
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              size="small"
              sx={{ p: 0.5 }}
              onClick={(e) => handleVendorCheckbox(vendor, e)}
              disableRipple
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flex: 1, ml: 0.5 }}
            >
              {vendor}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              {count}
            </Typography>
          </Box>
          <Collapse in={!isCollapsed}>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </Collapse>
        </li>
      );
    },
    [
      collapsedVendors,
      getVendorCheckboxState,
      vendorOptionsMap,
      toggleVendorCollapse,
      handleVendorCheckbox,
    ]
  );

  const PaperComponent = useCallback(
    (props: React.HTMLAttributes<HTMLElement>) => (
      <Paper {...props}>
        {props.children}
        <Divider />
        <Button
          fullWidth
          onClick={() => setOpen(false)}
          data-testid="scoring-products-done-btn"
          sx={{ py: 1, fontWeight: 700 }}
        >
          DONE
        </Button>
      </Paper>
    ),
    []
  );

  const isLoading =
    loading ||
    (inputValue.length >= MIN_CHARS && inputValue !== debouncedInput);

  const noOptionsText =
    inputValue.length < MIN_CHARS
      ? `Type at least ${MIN_CHARS} characters`
      : "No products found";

  return (
    <Autocomplete<ProductOption, true, false, false>
      id="products-autocomplete"
      multiple
      disableCloseOnSelect
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selectedOptions}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_, val, reason) => {
        if (reason !== "reset") setInputValue(val);
      }}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      onChange={(_, newValue) => onOptionsSelect(newValue)}
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      groupBy={(opt) => opt.vendorName ?? "Unknown"}
      renderGroup={renderGroup}
      PaperComponent={PaperComponent}
      size="small"
      renderTags={(tagValue, getTagProps) => {
        const maxVisible = 10;
        return (
          <>
            {tagValue.slice(0, maxVisible).map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option.label}
                  size="small"
                />
              );
            })}
            {tagValue.length > maxVisible && (
              <Chip
                label={`+${tagValue.length - maxVisible} more`}
                size="small"
                sx={{
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                }}
              />
            )}
          </>
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} style={{ paddingLeft: 48 }}>
            <Checkbox
              checked={selected}
              sx={{ marginRight: 1, padding: 0 }}
              disableRipple
            />
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          label="Search for Products"
        />
      )}
      slotProps={{
        listbox: {
          sx: {
            maxHeight: 300,
            ...lightScrollbarStyle,
          },
        },
      }}
    />
  );
};
